/**
 * /admin/ambassadors — لوحة إدارة السفراء.
 *
 * تبويبات:
 *   1) السفراء — قائمة كاملة + فلترة بحالة + تعديل (status, tier, custom_reward_rate, payout_mode, threshold, cookie_days)
 *   2) طلبات الدفع — صفحة مستقلّة /admin/payouts (super_admin) — رابط من التبويبات
 *   3) الإعدادات العامّة — toggles + قواعد المكافآت
 *
 * RLS تمنع غير الأدمن من رؤية البيانات؛ هذه الصفحة فوق ذلك تتحقّق من isAdmin
 * قبل العرض، وكلّ server function تتحقّق مرّة أخرى (دفاع عميق).
 *
 * النصوص بـ ar/en فقط (الفرنسيّة مخفيّة من الواجهة).
 */

import * as React from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { matchesAmbassadorSearch } from "@/lib/ambassador-search";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/lib/toast";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Loader2,
  CheckCircle2,
  XCircle,
  Wallet,
  Users,
  ChevronDown,
  ChevronUp,
  Settings,
  Eye,
  TrendingUp,
  Search,
  UserPlus,
  User,

} from "lucide-react";
import { useAdminPageAuth } from "@/hooks/useAdminPageAuth";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useLocale } from "@/contexts/LocaleContext";
import { SiteShell } from "@/components/SiteShell";
import {
  listAmbassadorsAdmin,
  updateAmbassadorAdmin,
  listAppSettingsAdmin,
  updateAppSettingAdmin,
  type UpdateAmbassadorInput,
} from "@/utils/admin.functions";


import { AdminMilestonesPanel } from "@/components/admin/AdminMilestonesPanel";
import { AdminNav } from "@/components/admin/AdminNav";
import { AmbassadorProfileSheet } from "@/components/admin/AmbassadorProfileSheet";
import { AmbassadorTiersPanel } from "@/components/admin/AmbassadorTiersPanel";
import { GuestAmbassadorsPanel } from "@/components/admin/GuestAmbassadorsPanel";
import { PartnerContractDialog } from "@/components/admin/PartnerContractDialog";
import { PartnerPayoutsPanel } from "@/components/admin/PartnerPayoutsPanel";
import { t } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { AmbassadorCodeBadge } from "@/components/ambassador/AmbassadorCodeBadge";

export const Route = createFileRoute("/admin/ambassadors")({
  component: AdminAmbassadorsPage,
  validateSearch: (search: Record<string, unknown>): { q?: string } =>
    typeof search.q === "string" && search.q.trim() ? { q: search.q } : {},

  head: () => ({ meta: [{ title: "Lecmo · إدارة السفراء" }] }),
});


type Tab = "ambassadors" | "rules" | "tiers" | "guests";

// مساعد ثنائيّ اللغة
const tr = (locale: string, ar: string, en: string) => (locale === "ar" ? ar : en);

// قواميس ترجمة قِيَم enums (تُعرَض للمستخدم بدل الإنجليزيّ الخام)
const STATUS_LABELS = {
  pending: { ar: "بانتظار المراجعة", en: "Pending" },
  approved: { ar: "معتمد", en: "Approved" },
  rejected: { ar: "مرفوض", en: "Rejected" },
  suspended: { ar: "موقوف", en: "Suspended" },
} as const;

const TIER_LABELS = {
  decouverte: { ar: "Lecmo Lover", en: "Lecmo Lover" },
  active: { ar: "Lecmo Ambassador", en: "Lecmo Ambassador" },
  elite: { ar: "Super Star Ambassador", en: "Super Star Ambassador" },
} as const;

const PAYOUT_MODE_LABELS = {
  none: { ar: "بدون", en: "None" },
  cash: { ar: "نقد", en: "Cash" },
  store_credit: { ar: "رصيد متجر", en: "Store credit" },
  both: { ar: "الإثنان", en: "Both" },
} as const;




const labelOf = <T extends Record<string, { ar: string; en: string }>>(
  map: T,
  key: string,
  locale: string,
): string => {
  const entry = map[key as keyof T];
  if (!entry) return key;
  return locale === "ar" ? entry.ar : entry.en;
};

function AdminAmbassadorsPage() {
  const { user, loading: authLoading } = useAdminPageAuth("/admin/ambassadors");
  const { isAdmin, loading: roleLoading } = useIsAdmin();
  const { locale, dir } = useLocale();
  const [tab, setTab] = React.useState<Tab>("ambassadors");

  if (authLoading || roleLoading) {
    return (
      <SiteShell>
        <div className="flex min-h-[60vh] items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      </SiteShell>
    );
  }

  if (!user) return null;

  if (!isAdmin) {
    return (
      <SiteShell>
        <div className="mx-auto max-w-2xl px-5 pb-24 pt-8 text-center" dir={dir}>
          <h1 className="t-h1 text-foreground">403</h1>
          <p className="mt-4 text-sm text-muted-foreground">
            {tr(locale, "هذه الصفحة للمشرفين فقط.", "Admin access required.")}
          </p>
          <Link to="/account" className="mt-6 inline-block text-sm text-primary underline">
            {tr(locale, "العودة", "Back")}
          </Link>
        </div>
      </SiteShell>
    );
  }

  return (
    <SiteShell>
      <div className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6" dir={dir}>
        <AdminNav />
        <div className="mb-8">
          <p className="t-eyebrow-muted">{tr(locale, "الإدارة", "Admin")}</p>
          <h1 className="mt-2 t-h1 text-foreground">
            {tr(locale, "السفراء", "Ambassadors")}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {tr(locale, "إدارة السفراء وطلبات الدفع.", "Manage ambassadors and payout requests.")}
          </p>
        </div>

        <ProgramKillSwitchBanner />
        <ProgramPauseNoticeEditor />


        {/* Tabs */}
        <div className="mb-6 flex gap-1 border-b border-border overflow-x-auto">
          <TabButton
            active={tab === "ambassadors"}
            onClick={() => setTab("ambassadors")}
            icon={<Users className="h-4 w-4" />}
            label={tr(locale, "السفراء", "Ambassadors")}
          />
          <Link
            to="/admin/payouts"
            className="flex items-center gap-2 border-b-2 border-transparent px-4 py-2.5 text-sm text-muted-foreground hover:text-foreground"
          >
            <Wallet className="h-4 w-4" />
            {tr(locale, "طلبات الدفع", "Payout Requests")}
          </Link>
          <TabButton
            active={tab === "tiers"}
            onClick={() => setTab("tiers")}
            icon={<TrendingUp className="h-4 w-4" />}
            label={tr(locale, "الشرائح", "Tiers")}
          />
          <TabButton
            active={tab === "guests"}
            onClick={() => setTab("guests")}
            icon={<UserPlus className="h-4 w-4" />}
            label={tr(locale, "ناقصو البيانات", "Incomplete")}
          />
          <TabButton
            active={tab === "rules"}
            onClick={() => setTab("rules")}
            icon={<Settings className="h-4 w-4" />}
            label={tr(locale, "الإعدادات العامّة", "Global Settings")}
          />
        </div>

        {tab === "ambassadors" && <AmbassadorsList />}

        {tab === "guests" && <GuestAmbassadorsPanel />}
        {tab === "tiers" && <AmbassadorTiersPanel />}
        {tab === "rules" && (
          <div className="space-y-6">
            <AdminMilestonesPanel />
          </div>
        )}
      </div>
    </SiteShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function TabButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-center gap-2 px-4 py-2.5 text-sm transition-colors",
        active
          ? "border-b-2 border-primary text-foreground"
          : "border-b-2 border-transparent text-muted-foreground hover:text-foreground",
      )}
    >
      {icon}
      {label}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// قائمة السفراء
// ─────────────────────────────────────────────────────────────────────────────
type AmbassadorRow = Awaited<
  ReturnType<typeof listAmbassadorsAdmin>
>["ambassadors"][number];

/**
 * يحدّد ما إذا كان السفير «نشطاً» فعلاً (استخدم كوده) أم مجرّد عميل سجّل
 * حساباً ولم يفعّل نشاطه السفيريّ بعد. كلّ مستخدم تقنياً سفير بفعل
 * handle_new_user، لذا التمييز هنا سلوكيّ لا بنيويّ.
 */
function isActiveAmbassador(a: AmbassadorRow): boolean {
  if ((a.stats?.clicks ?? 0) > 0 || (a.stats?.sales ?? 0) > 0) return true;
  if (a.tier && a.tier !== "decouverte") return true;
  if (a.relationship_type && a.relationship_type !== "organic") return true;
  return false;
}

function AmbassadorsList() {
  const { locale } = useLocale();
  const navigate = useNavigate();
  const { q: urlQuery } = Route.useSearch();
  const [query, setQuery] = React.useState(urlQuery ?? "");

  // مزامنة الرابط ← الحالة (عند الوصول من بحث الناف بار)
  React.useEffect(() => {
    setQuery(urlQuery ?? "");
  }, [urlQuery]);

  const onQueryChange = (value: string) => {
    setQuery(value);
    navigate({
      to: "/admin/ambassadors",
      search: { q: value.trim() ? value.trim() : undefined },
      replace: true,
    });
  };

  const [filter, setFilter] = React.useState<"all" | "pending" | "approved" | "rejected" | "suspended">("all");
  const [relFilter, setRelFilter] = React.useState<"all" | "organic" | "signature_partner" | "vip_guest">("all");
  const [tierFilter, setTierFilter] = React.useState<"all" | "decouverte" | "active" | "elite">("all");
  const [channelFilter, setChannelFilter] = React.useState<"all" | "direct" | "uppromote">("all");
  const [buyFilter, setBuyFilter] = React.useState<"all" | "buyers" | "non_buyers">("all");
  const [expanded, setExpanded] = React.useState<string | null>(null);
  const [profileId, setProfileId] = React.useState<string | null>(null);



  const { data, isLoading } = useQuery({
    queryKey: ["admin-ambassadors"],
    queryFn: () => listAmbassadorsAdmin(),
    staleTime: 30_000,
  });

  // قراءة مفاتيح app_settings لإخفاء/إظهار الحقول الشرطيّة في كلّ بطاقة
  const { data: settingsData } = useQuery({
    queryKey: ["admin-app-settings"],
    queryFn: () => listAppSettingsAdmin(),
    staleTime: 30_000,
  });
  const overridesEnabled =
    settingsData?.settings.find((s) => s.key === "ambassador_override_enabled")?.value === true;
  const customCookieEnabled =
    settingsData?.settings.find((s) => s.key === "ambassador_custom_cookie_enabled")?.value === true;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const all = data?.ambassadors ?? [];
  const filtered = all.filter((a) => {
    if (filter !== "all" && a.status !== filter) return false;
    if (relFilter !== "all" && (a.relationship_type ?? "organic") !== relFilter) return false;
    if (tierFilter !== "all" && (a.tier ?? "decouverte") !== tierFilter) return false;
    if (channelFilter !== "all" && (a.external_channel ?? "direct") !== channelFilter) return false;
    if (buyFilter === "buyers" && (a.purchases?.count ?? 0) === 0) return false;
    if (buyFilter === "non_buyers" && (a.purchases?.count ?? 0) > 0) return false;
    if (!matchesAmbassadorSearch(a, query)) return false;
    return true;
  });

  const buyCounts = {
    all: all.length,
    buyers: all.filter((a) => (a.purchases?.count ?? 0) > 0).length,
    non_buyers: all.filter((a) => (a.purchases?.count ?? 0) === 0).length,
  };
  const buyFilterLabels: Record<typeof buyFilter, string> = {
    all: tr(locale, "الشراء · الكلّ", "Purchases · All"),
    buyers: tr(locale, "اشترى", "Bought"),
    non_buyers: tr(locale, "لم يشترِ", "Never bought"),
  };



  const counts = {
    all: all.length,
    pending: all.filter((a) => a.status === "pending").length,
    approved: all.filter((a) => a.status === "approved").length,
    rejected: all.filter((a) => a.status === "rejected").length,
    suspended: all.filter((a) => a.status === "suspended").length,
  };

  const relCounts = {
    all: all.length,
    organic: all.filter((a) => (a.relationship_type ?? "organic") === "organic").length,
    signature_partner: all.filter((a) => a.relationship_type === "signature_partner").length,
    vip_guest: all.filter((a) => a.relationship_type === "vip_guest").length,
  };

  const tierCounts = {
    all: all.length,
    decouverte: all.filter((a) => (a.tier ?? "decouverte") === "decouverte").length,
    active: all.filter((a) => a.tier === "active").length,
    elite: all.filter((a) => a.tier === "elite").length,
  };

  const channelCounts = {
    all: all.length,
    direct: all.filter((a) => (a.external_channel ?? "direct") === "direct").length,
    uppromote: all.filter((a) => a.external_channel === "uppromote").length,
  };

  const filterLabels: Record<typeof filter, string> = {
    all: tr(locale, "الكلّ", "All"),
    pending: labelOf(STATUS_LABELS, "pending", locale),
    approved: labelOf(STATUS_LABELS, "approved", locale),
    rejected: labelOf(STATUS_LABELS, "rejected", locale),
    suspended: labelOf(STATUS_LABELS, "suspended", locale),
  };

  const relFilterLabels: Record<typeof relFilter, string> = {
    all: t(locale, "admin.ambassadors.relationship.filter_all"),
    organic: t(locale, "ambassador.relationship.organic"),
    signature_partner: t(locale, "ambassador.relationship.signature_partner"),
    vip_guest: t(locale, "ambassador.relationship.vip_guest"),
  };

  const tierFilterLabels: Record<typeof tierFilter, string> = {
    all: tr(locale, "كلّ الشرائح", "All tiers"),
    decouverte: "Lecmo Lover",
    active: "Lecmo Ambassador",
    elite: "Super Star Ambassador",
  };

  const channelFilterLabels: Record<typeof channelFilter, string> = {
    all: t(locale, "admin.uppromote.filter.channel") + " · " + tr(locale, "الكلّ", "All"),
    direct: t(locale, "admin.uppromote.channel.direct"),
    uppromote: t(locale, "admin.uppromote.channel.uppromote"),
  };

  return (
    <div>
      {/* بحث موحَّد: كود · اسم · بريد · سوشيال */}
      <div className="mb-3 flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute top-1/2 -translate-y-1/2 start-3 h-4 w-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder={tr(
              locale,
              "ابحث بالكود أو الاسم أو البريد…",
              "Search by code, name or email…",
            )}
            className="ps-9"
            aria-label={tr(locale, "بحث عن سفير", "Search ambassadors")}
          />
        </div>
        {query.trim() ? (
          <button
            type="button"
            onClick={() => onQueryChange("")}
            className="min-h-[36px] rounded border border-border px-3 text-xs text-muted-foreground hover:text-foreground"
          >
            {tr(locale, "مسح", "Clear")}
          </button>
        ) : null}
        <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
          {filtered.length} / {all.length}
        </span>
      </div>
      {/* الحالة — إدارة برنامج السفراء */}

      <div className="mb-3 flex flex-wrap gap-2">
        {(Object.keys(counts) as (keyof typeof counts)[]).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setFilter(k)}
            className={cn(
              "rounded-full border px-3 py-1 text-xs transition-colors",
              filter === k
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground",
            )}
          >
            {filterLabels[k]} · {counts[k]}
          </button>
        ))}
      </div>
      {/* الشريحة — أداء السفير */}
      <div className="mb-3 flex flex-wrap gap-2">
        {(Object.keys(tierCounts) as (keyof typeof tierCounts)[]).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setTierFilter(k)}
            className={cn(
              "rounded-full border px-3 py-1 text-[11px] transition-colors",
              tierFilter === k
                ? "border-amber-600 bg-amber-600 text-white"
                : "border-border bg-card text-muted-foreground hover:border-amber-600/40 hover:text-foreground",
            )}
          >
            {tierFilterLabels[k]} · {tierCounts[k]}
          </button>
        ))}
      </div>
      {/* نوع العلاقة — Organic / Signature Partner / VIP Guest */}
      <div className="mb-3 flex flex-wrap gap-2">
        {(Object.keys(relCounts) as (keyof typeof relCounts)[]).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setRelFilter(k)}
            className={cn(
              "rounded-full border px-3 py-1 text-[11px] transition-colors",
              relFilter === k
                ? "border-foreground bg-foreground text-background"
                : "border-border bg-card text-muted-foreground hover:border-foreground/40 hover:text-foreground",
            )}
          >
            {relFilterLabels[k]} · {relCounts[k]}
          </button>
        ))}
      </div>
      {/* مصدر السفير — Direct / UpPromote */}
      <div className="mb-4 flex flex-wrap gap-2">
        {(Object.keys(channelCounts) as (keyof typeof channelCounts)[]).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setChannelFilter(k)}
            className={cn(
              "rounded-full border px-3 py-1 text-[11px] transition-colors",
              channelFilter === k
                ? "border-indigo-600 bg-indigo-600 text-white"
                : "border-border bg-card text-muted-foreground hover:border-indigo-600/40 hover:text-foreground",
            )}
          >
            {channelFilterLabels[k]} · {channelCounts[k]}
          </button>
        ))}
      </div>
      {/* الشراء الشخصيّ — اشترى / لم يشترِ */}
      <div className="mb-4 flex flex-wrap gap-2">
        {(Object.keys(buyCounts) as (keyof typeof buyCounts)[]).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setBuyFilter(k)}
            className={cn(
              "rounded-full border px-3 py-1 text-[11px] transition-colors",
              buyFilter === k
                ? "border-success bg-success text-success-foreground"
                : "border-border bg-card text-muted-foreground hover:border-success/40 hover:text-foreground",
            )}
          >
            {buyFilterLabels[k]} · {buyCounts[k]}
          </button>
        ))}
      </div>





      {filtered.length === 0 ? (
        <div className="border border-dashed border-border p-12 text-center text-sm text-muted-foreground">
          {query.trim()
            ? tr(
                locale,
                `لا يوجد سفير يطابق «${query.trim()}».`,
                `No ambassador matches “${query.trim()}”.`,
              )
            : tr(locale, "لا توجد بيانات لهذا الفلتر.", "No ambassadors match this filter.")}
        </div>

      ) : (
        <div className="space-y-2">
          {filtered.map((a) => (
            <AmbassadorRowCard
              key={a.id}
              row={a}
              isExpanded={expanded === a.id}
              onToggle={() => setExpanded((v) => (v === a.id ? null : a.id))}
              overridesEnabled={overridesEnabled}
              customCookieEnabled={customCookieEnabled}
              onOpenProfile={() => setProfileId(a.id)}
            />
          ))}
        </div>
      )}

      <AmbassadorProfileSheet ambassadorId={profileId} onClose={() => setProfileId(null)} />
    </div>
  );
}


function StatusBadge({ status }: { status: string }) {
  const { locale } = useLocale();
  const classes: Record<string, string> = {
    pending: "bg-warning/15 text-warning",
    approved: "bg-success/15 text-success",
    rejected: "bg-destructive/15 text-destructive",
    suspended: "bg-muted text-muted-foreground",
  };
  const label = labelOf(STATUS_LABELS, status, locale) || status;
  const cls = classes[status] ?? "bg-muted text-muted-foreground";
  return <span className={cn("rounded px-2 py-0.5 text-xs font-medium", cls)}>{label}</span>;
}

function RelationshipBadge({ type }: { type: string | null | undefined }) {
  const { locale } = useLocale();
  const t_ = type ?? "organic";
  if (t_ === "organic") return null; // لا نعرض شارة للسفير العاديّ (هو الافتراضيّ)
  const cls =
    t_ === "signature_partner"
      ? "bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/40"
      : "bg-purple-500/15 text-purple-700 dark:text-purple-400 border border-purple-500/40";
  const label = t(locale, `ambassador.relationship.${t_}`);
  return <span className={cn("rounded px-1.5 py-0.5 text-[10px] font-medium", cls)}>{label}</span>;
}

function AmbassadorRowCard({
  row,
  isExpanded,
  onToggle,
  overridesEnabled,
  customCookieEnabled,
  onOpenProfile,
}: {
  row: AmbassadorRow;
  isExpanded: boolean;
  onToggle: () => void;
  overridesEnabled: boolean;
  customCookieEnabled: boolean;
  onOpenProfile: () => void;
}) {

  const { locale, formatPrice } = useLocale();
  const qc = useQueryClient();
  const [convertMode, setConvertMode] = React.useState<
    null | "to_partner" | "to_guest" | "to_organic"
  >(null);

  const updateMut = useMutation({
    mutationFn: (data: UpdateAmbassadorInput) => updateAmbassadorAdmin({ data }),
    onSuccess: () => {
      toast.success(tr(locale, "تمّ التحديث", "Updated"));
      qc.invalidateQueries({ queryKey: ["admin-ambassadors"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="border border-border bg-card">
      <div className="flex items-center justify-between gap-3 p-4">
        <button
          type="button"
          onClick={onToggle}
          className="flex flex-1 items-center gap-3 text-start"
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="t-body font-medium text-foreground truncate">{row.display_name}</p>
              <StatusBadge status={row.status} />
              <RelationshipBadge type={row.relationship_type} />
              <AmbassadorCodeBadge code={row.code} tier={row.tier} size="sm" />
              {isActiveAmbassador(row) ? (
                <span className="rounded px-1.5 py-0.5 text-[10px] font-medium bg-success/15 text-success">
                  {tr(locale, "نشط", "Active")}
                </span>
              ) : (
                <span className="rounded px-1.5 py-0.5 text-[10px] font-medium bg-muted text-muted-foreground">
                  {tr(locale, "عميل فقط", "Customer only")}
                </span>
              )}
              {(row.purchases?.count ?? 0) > 0 ? (
                <span className="rounded px-1.5 py-0.5 text-[10px] font-medium bg-success/15 text-success">
                  🛍️ {tr(locale, "اشترى (مدفوع)", "Paid")} · {row.purchases.count}
                </span>
              ) : (row.purchases?.pendingCount ?? 0) > 0 ? (
                <span className="rounded px-1.5 py-0.5 text-[10px] font-medium bg-amber-500/15 text-amber-600">
                  ⏳ {tr(locale, "طلب غير مدفوع", "Unpaid order")} · {row.purchases.pendingCount}
                </span>
              ) : (row.purchases?.cancelledCount ?? 0) > 0 ? (
                <span className="rounded px-1.5 py-0.5 text-[10px] font-medium bg-muted text-muted-foreground">
                  {tr(locale, "طلب ملغى فقط", "Cancelled only")}
                </span>
              ) : (
                <span className="rounded px-1.5 py-0.5 text-[10px] font-medium bg-muted text-muted-foreground">
                  {tr(locale, "لم يشترِ", "Never bought")}
                </span>
              )}
            </div>
            {row.email && (
              <p className="mt-0.5 text-xs text-muted-foreground truncate">{row.email}</p>
            )}
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
              {(row.purchases?.count ?? 0) > 0 && (
                <>
                  <span>
                    {tr(locale, "إجمالي مشترياته", "Spent")}:{" "}
                    <strong className="text-foreground">
                      {formatPrice(String(row.purchases.total), row.purchases.currency ?? "AED")}
                    </strong>
                  </span>
                  {row.purchases.lastAt && (
                    <span>
                      {tr(locale, "آخر شراء", "Last order")}:{" "}
                      <strong className="text-foreground">
                        {new Date(row.purchases.lastAt).toLocaleDateString(
                          locale === "ar" ? "ar" : "en-GB",
                          { day: "2-digit", month: "short", year: "numeric" },
                        )}
                      </strong>
                    </span>
                  )}
                </>
              )}

              <span>
                {tr(locale, "الدرجة", "Tier")}:{" "}
                <strong className="text-foreground">
                  {row.tier === "elite"
                    ? "Super Star Ambassador"
                    : row.tier === "active"
                    ? "Lecmo Ambassador"
                    : "Lecmo Lover"}
                </strong>
              </span>
              <span>
                {tr(locale, "تاريخ الالتحاق", "Joined")}:{" "}
                <strong className="text-foreground">
                  {new Date(row.created_at).toLocaleDateString(
                    locale === "ar" ? "ar" : "en-GB",
                    { day: "2-digit", month: "short", year: "numeric" }
                  )}
                </strong>
              </span>
              <span>
                {tr(locale, "نقرات", "Clicks")}: <strong className="text-foreground">{row.stats.clicks}</strong>
              </span>
              <span>
                {tr(locale, "مبيعات", "Sales")}: <strong className="text-foreground">{row.stats.sales}</strong>
              </span>
              <span>
                {tr(locale, "تحويل", "CVR")}:{" "}
                <strong className="text-foreground">
                  {row.stats.clicks > 0
                    ? ((row.stats.sales / row.stats.clicks) * 100).toFixed(1)
                    : "0.0"}
                  %
                </strong>
              </span>
              {row.stats.revenue > 0 && (
                <span>
                  {tr(locale, "إيراد", "Revenue")}:{" "}
                  <strong className="text-foreground">
                    {row.stats.revenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </strong>
                </span>
              )}
              {row.balances[0] && (
                <span>
                  {tr(locale, "متاح", "Available")}:{" "}
                  <strong className="text-foreground">
                    {formatPrice(String(row.balances[0].available_credit), row.balances[0].currency)}
                  </strong>
                </span>
              )}
            </div>

          </div>
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </button>

        {/* الملفّ الموحَّد — لوح جانبيّ داخل نفس الصفحة، يعمل مع/بلا حساب */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onOpenProfile();
          }}
          className="flex shrink-0 items-center gap-1 rounded border border-primary bg-primary/10 px-2.5 py-1 text-xs text-primary hover:bg-primary/20"
          title={tr(locale, "الملفّ الكامل لهذا العميل", "Full customer profile")}
        >
          <User className="h-3.5 w-3.5" />
          {tr(locale, "الملفّ الكامل", "Full profile")}
        </button>




        {row.status === "pending" && (
          <div className="flex gap-1.5 shrink-0">
            <button
              type="button"
              disabled={updateMut.isPending}
              onClick={() => updateMut.mutate({ ambassadorId: row.id, status: "approved" })}
              className="flex items-center gap-1 rounded border border-success bg-success/10 px-2.5 py-1 text-xs text-success hover:bg-success/20 disabled:opacity-50"
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              {tr(locale, "اعتمد", "Approve")}
            </button>
            <button
              type="button"
              disabled={updateMut.isPending}
              onClick={() => updateMut.mutate({ ambassadorId: row.id, status: "rejected" })}
              className="flex items-center gap-1 rounded border border-destructive bg-destructive/10 px-2.5 py-1 text-xs text-destructive hover:bg-destructive/20 disabled:opacity-50"
            >
              <XCircle className="h-3.5 w-3.5" />
              {tr(locale, "ارفض", "Reject")}
            </button>
          </div>
        )}

        {row.status === "approved" && (
          <>
            <Link
              to="/admin/ambassador-monitor"
              search={{ id: row.id, range: "30d" as const }}
              onClick={(e) => e.stopPropagation()}
              className="flex shrink-0 items-center gap-1 rounded border border-primary/50 px-2.5 py-1 text-xs text-primary hover:bg-primary/10"
              title={tr(locale, "راقب هذا السفير (لوحة الأدمن)", "Monitor this ambassador (admin dashboard)")}
            >
              📊 {tr(locale, "راقب", "Monitor")}
            </Link>
            <Link
              to="/admin/preview-as-ambassador"
              search={{ id: row.id }}
              onClick={(e) => e.stopPropagation()}
              className="flex shrink-0 items-center gap-1 rounded border border-border px-2.5 py-1 text-xs text-foreground hover:border-primary hover:text-primary"
              title={tr(locale, "معاينة كهذا السفير", "Preview as this ambassador")}
            >
              <Eye className="h-3.5 w-3.5" />
              {tr(locale, "معاينة", "Preview")}
            </Link>
          </>
        )}
      </div>

      {isExpanded && (
        <>
          <ExpandedEditor
            row={row}
            onSave={(d) => updateMut.mutate(d)}
            pending={updateMut.isPending}
            overridesEnabled={overridesEnabled}
            customCookieEnabled={customCookieEnabled}
          />

          {/* Convert actions */}
          <div className="border-t border-border bg-muted/20 px-4 py-3 flex flex-wrap gap-2">
            {row.relationship_type !== "signature_partner" && (
              <button
                type="button"
                onClick={() => setConvertMode("to_partner")}
                className="rounded border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-xs text-amber-700 dark:text-amber-400 hover:bg-amber-500/20"
              >
                {t(locale, "admin.ambassadors.convert.cta")}
              </button>
            )}
            {row.relationship_type !== "vip_guest" && (
              <button
                type="button"
                onClick={() => setConvertMode("to_guest")}
                className="rounded border border-purple-500/40 bg-purple-500/10 px-2.5 py-1 text-xs text-purple-700 dark:text-purple-400 hover:bg-purple-500/20"
              >
                {t(locale, "admin.ambassadors.convert.to_guest")}
              </button>
            )}
            {row.relationship_type !== "organic" && (
              <button
                type="button"
                onClick={() => setConvertMode("to_organic")}
                className="rounded border border-border bg-card px-2.5 py-1 text-xs text-muted-foreground hover:text-foreground"
              >
                {t(locale, "admin.ambassadors.convert.to_organic")}
              </button>
            )}
          </div>

          {/* Partner payouts panel — only for signature partners */}
          {row.relationship_type === "signature_partner" && (
            <div className="border-t border-border p-4">
              <PartnerPayoutsPanel
                ambassadorId={row.id}
                ambassadorName={row.display_name}
                contractEndsAt={row.contract_ends_at ?? null}
              />
            </div>
          )}
        </>
      )}

      {convertMode && (
        <PartnerContractDialog
          open={true}
          onClose={() => setConvertMode(null)}
          mode={convertMode}
          ambassadorId={row.id}
          ambassadorName={row.display_name}
          initial={{
            contract_starts_at: row.contract_starts_at ?? null,
            contract_ends_at: row.contract_ends_at ?? null,
            monthly_fee_aed: row.monthly_fee_aed ?? null,
            deliverables_note: row.deliverables_note ?? null,
            seeding_value_aed: row.seeding_value_aed ?? null,
          }}
        />
      )}
    </div>
  );
}

function ExpandedEditor({
  row,
  onSave,
  pending,
  overridesEnabled,
  customCookieEnabled,
}: {
  row: AmbassadorRow;
  onSave: (d: UpdateAmbassadorInput) => void;
  pending: boolean;
  overridesEnabled: boolean;
  customCookieEnabled: boolean;
}) {
  const { locale, formatPrice } = useLocale();
  const [tier, setTier] = React.useState<"decouverte" | "active" | "elite">(row.tier);
  const [payoutMode, setPayoutMode] = React.useState<"none" | "cash" | "store_credit" | "both">(
    row.payout_mode,
  );
  const [rate, setRate] = React.useState<string>(
    row.custom_reward_rate != null ? String(row.custom_reward_rate) : "",
  );
  const [threshold, setThreshold] = React.useState<string>(
    row.custom_payout_threshold != null ? String(row.custom_payout_threshold) : "",
  );
  const [cookieDays, setCookieDays] = React.useState<string>(
    row.custom_cookie_days != null ? String(row.custom_cookie_days) : "",
  );
  const [statusEdit, setStatusEdit] = React.useState<typeof row.status>(row.status);

  const handleSave = () => {
    onSave({
      ambassadorId: row.id,
      status: statusEdit as "pending" | "approved" | "rejected" | "suspended",
      tier,
      payout_mode: payoutMode,
      // لا نُرسل custom_reward_rate إلّا لو الـoverride مُفعَّل (نُحافظ على القيمة المخزَّنة)
      ...(overridesEnabled
        ? { custom_reward_rate: rate.trim() === "" ? null : Number(rate) }
        : {}),
      // كذلك للـcustom_cookie_days — نُرسلها فقط لو الـtoggle مُفعَّل
      ...(customCookieEnabled
        ? { custom_cookie_days: cookieDays.trim() === "" ? null : Number(cookieDays) }
        : {}),
      custom_payout_threshold: threshold.trim() === "" ? null : Number(threshold),
    });
  };

  const labels = {
    status: tr(locale, "الحالة", "Status"),
    tier: tr(locale, "الرتبة", "Tier"),
    payoutMode: tr(locale, "طريقة الدفع", "Payout mode"),
    rate: tr(locale, "نسبة المكافأة المخصّصة (٪)", "Custom reward rate (%)"),
    threshold: tr(locale, "حدّ أدنى للسحب", "Payout threshold"),
    cookieDays: tr(locale, "مدّة الكوكي (يوم)", "Cookie duration (days)"),
    save: tr(locale, "حفظ", "Save"),
    balances: tr(locale, "الأرصدة", "Balances"),
    available: tr(locale, "متاح", "Available"),
    pendingBal: tr(locale, "معلَّق", "Pending"),
    paidOut: tr(locale, "مدفوع", "Paid out"),
  };

  return (
    <div className="border-t border-border bg-muted/30 p-4 space-y-4">
      {/* أرصدة */}
      <div>
        <p className="mb-2 t-eyebrow-muted">{labels.balances}</p>
        {row.balances.length === 0 ? (
          <p className="text-xs text-muted-foreground">—</p>
        ) : (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {row.balances.map((b) => (
              <div key={b.currency} className="rounded border border-border bg-card p-2.5 text-xs">
                <p className="font-mono text-[10px] uppercase text-muted-foreground">{b.currency}</p>
                <p className="mt-1 text-foreground">
                  {labels.available}:{" "}
                  <span className="font-medium">
                    {formatPrice(String(b.available_credit), b.currency)}
                  </span>
                </p>
                <p className="text-muted-foreground">
                  {labels.pendingBal}: {formatPrice(String(b.pending_credit), b.currency)}
                </p>
                <p className="text-muted-foreground">
                  {labels.paidOut}: {formatPrice(String(b.paid_out), b.currency)}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* فورم */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label={labels.status}>
          <select
            value={statusEdit}
            onChange={(e) => setStatusEdit(e.target.value as typeof row.status)}
            className="w-full rounded border border-border bg-background px-2.5 py-1.5 text-sm"
          >
            {(["pending", "approved", "rejected", "suspended"] as const).map((s) => (
              <option key={s} value={s}>
                {labelOf(STATUS_LABELS, s, locale)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={labels.tier}>
          <select
            value={tier}
            onChange={(e) => setTier(e.target.value as typeof tier)}
            className="w-full rounded border border-border bg-background px-2.5 py-1.5 text-sm"
          >
            {(["decouverte", "active", "elite"] as const).map((tt) => (
              <option key={tt} value={tt}>
                {labelOf(TIER_LABELS, tt, locale)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={labels.payoutMode}>
          <select
            value={payoutMode}
            onChange={(e) => setPayoutMode(e.target.value as typeof payoutMode)}
            className="w-full rounded border border-border bg-background px-2.5 py-1.5 text-sm"
          >
            {(["none", "cash", "store_credit", "both"] as const).map((m) => (
              <option key={m} value={m}>
                {labelOf(PAYOUT_MODE_LABELS, m, locale)}
              </option>
            ))}
          </select>
        </Field>
        {overridesEnabled && (
          <Field label={labels.rate}>
            <input
              type="number"
              step="0.1"
              min="0"
              max="100"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              placeholder="—"
              className="w-full rounded border border-border bg-background px-2.5 py-1.5 text-sm"
            />
          </Field>
        )}
        <Field label={labels.threshold}>
          <input
            type="number"
            step="1"
            min="0"
            value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
            placeholder="—"
            className="w-full rounded border border-border bg-background px-2.5 py-1.5 text-sm"
          />
        </Field>
        {customCookieEnabled && (
          <Field label={labels.cookieDays}>
            <input
              type="number"
              step="1"
              min="1"
              max="120"
              value={cookieDays}
              onChange={(e) => setCookieDays(e.target.value)}
              placeholder={tr(locale, "افتراضي ٣٠", "Default 30")}
              className="w-full rounded border border-border bg-background px-2.5 py-1.5 text-sm"
            />
          </Field>
        )}
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={pending}
          className="flex items-center gap-2 bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {pending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {labels.save}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

// طلبات السحب لها صفحة واحدة فقط: /admin/payouts (super_admin) — لا نسخة مكرّرة هنا.


// ─────────────────────────────────────────────────────────────────────────────
// بانر مفتاح الإيقاف المؤقّت للبرنامج
// ─────────────────────────────────────────────────────────────────────────────
function ProgramKillSwitchBanner() {
  const { locale } = useLocale();
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["admin-app-settings"],
    queryFn: () => listAppSettingsAdmin(),
    staleTime: 30_000,
  });

  const setting = data?.settings.find((s) => s.key === "ambassador_program_enabled");
  // الافتراض: مفعَّل (true) ما لم يُخزَّن false صراحةً
  const enabled = setting?.value !== false;

  const mutation = useMutation({
    mutationFn: (value: boolean) =>
      updateAppSettingAdmin({ data: { key: "ambassador_program_enabled", value } }),
    onSuccess: (_, value) => {
      qc.invalidateQueries({ queryKey: ["admin-app-settings"] });
      toast.success(
        value
          ? tr(locale, "تمّ تشغيل البرنامج", "Program enabled")
          : tr(locale, "تمّ إيقاف البرنامج مؤقّتاً", "Program paused"),
      );
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : tr(locale, "فشل الحفظ", "Save failed")),
  });

  return (
    <div
      className={cn(
        "mb-6 flex flex-wrap items-center justify-between gap-4 rounded-none border p-4",
        enabled
          ? "border-border bg-card"
          : "border-destructive/40 bg-destructive/5",
      )}
    >
      <div className="flex-1 min-w-[220px]">
        <div className="flex items-center gap-2">
          <span className={cn("h-2 w-2 rounded-full", enabled ? "bg-emerald-500" : "bg-destructive")} />
          <p className="text-sm font-medium text-foreground">
            {enabled
              ? tr(locale, "برنامج السفراء: مفعَّل", "Ambassador program: Active")
              : tr(locale, "برنامج السفراء: موقوف مؤقّتاً", "Ambassador program: Paused")}
          </p>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {enabled
            ? tr(
                locale,
                "التتبّع وقيد المكافآت يعملان بشكل طبيعيّ. الأرصدة والسحوبات السابقة لا تتأثّر عند الإيقاف.",
                "Tracking and reward credits are running normally. Existing balances and payouts are unaffected by pausing.",
              )
            : tr(
                locale,
                "لن يُضاف أيّ ائتمان جديد للسفراء من طلبات Shopify. الأرصدة الحاليّة والسحوبات المعلَّقة كما هي.",
                "No new ambassador credit will be added from Shopify orders. Existing balances and pending payouts remain intact.",
              )}
        </p>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-xs text-muted-foreground">
          {enabled ? tr(locale, "مفعَّل", "On") : tr(locale, "متوقّف", "Off")}
        </span>
        <Switch
          checked={enabled}
          disabled={mutation.isPending}
          onCheckedChange={(v) => mutation.mutate(v)}
        />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// محرّر رسالة إيقاف البرنامج (تظهر للعميل في /account/ambassador و /account/media)
// ─────────────────────────────────────────────────────────────────────────────
type PauseNotice = {
  show: boolean;
  title_ar: string;
  title_en: string;
  body_ar: string;
  body_en: string;
};

const DEFAULT_PAUSE_NOTICE: PauseNotice = {
  show: false,
  title_ar: "برنامج السفراء والولاء متوقّف مؤقّتاً",
  title_en: "Ambassador & loyalty program paused",
  body_ar: "رصيدك ونقاطك محفوظة بالكامل. سنعيد التفعيل قريباً.",
  body_en: "Your balance and points are fully preserved. We will resume shortly.",
};

export function ProgramPauseNoticeEditor() {
  const { locale } = useLocale();
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["admin-app-settings"],
    queryFn: () => listAppSettingsAdmin(),
    staleTime: 30_000,
  });

  const stored = data?.settings.find((s) => s.key === "public_ambassador_pause_notice")?.value as
    | Partial<PauseNotice>
    | undefined;

  const initial: PauseNotice = React.useMemo(
    () => ({ ...DEFAULT_PAUSE_NOTICE, ...(stored ?? {}) }),
    [stored],
  );

  const [draft, setDraft] = React.useState<PauseNotice>(initial);
  const [dirty, setDirty] = React.useState(false);

  React.useEffect(() => {
    setDraft(initial);
    setDirty(false);
  }, [initial]);

  const update = (patch: Partial<PauseNotice>) => {
    setDraft((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  };

  const saveMutation = useMutation({
    mutationFn: () =>
      updateAppSettingAdmin({
        data: { key: "public_ambassador_pause_notice", value: draft },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-app-settings"] });
      qc.invalidateQueries({ queryKey: ["public-setting", "public_ambassador_pause_notice"] });
      setDirty(false);
      toast.success(tr(locale, "تمّ حفظ الرسالة", "Notice saved"));
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : tr(locale, "فشل الحفظ", "Save failed")),
  });

  return (
    <div className="mb-6 rounded-none border border-border bg-card p-4">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-3">
        <div className="min-w-[220px]">
          <p className="text-sm font-medium text-foreground">
            {tr(locale, "📢 رسالة الإيقاف للعميل", "📢 Customer pause notice")}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {tr(
              locale,
              "تظهر أعلى صفحات السفير (`/account/ambassador` و`/account/media`) عند تفعيلها.",
              "Shown at the top of ambassador pages (`/account/ambassador` and `/account/media`) when enabled.",
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {draft.show ? tr(locale, "ظاهرة", "Visible") : tr(locale, "مخفيّة", "Hidden")}
          </span>
          <Switch checked={draft.show} onCheckedChange={(v) => update({ show: v })} />
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">
            {tr(locale, "العنوان (عربي)", "Title (Arabic)")}
          </Label>
          <Input
            value={draft.title_ar}
            onChange={(e) => update({ title_ar: e.target.value })}
            dir="rtl"
            maxLength={100}
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">
            {tr(locale, "العنوان (إنجليزي)", "Title (English)")}
          </Label>
          <Input
            value={draft.title_en}
            onChange={(e) => update({ title_en: e.target.value })}
            dir="ltr"
            maxLength={100}
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">
            {tr(locale, "الرسالة (عربي)", "Body (Arabic)")}
          </Label>
          <Textarea
            value={draft.body_ar}
            onChange={(e) => update({ body_ar: e.target.value })}
            dir="rtl"
            rows={3}
            maxLength={300}
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">
            {tr(locale, "الرسالة (إنجليزي)", "Body (English)")}
          </Label>
          <Textarea
            value={draft.body_en}
            onChange={(e) => update({ body_en: e.target.value })}
            dir="ltr"
            rows={3}
            maxLength={300}
          />
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <Button
          size="sm"
          onClick={() => saveMutation.mutate()}
          disabled={!dirty || saveMutation.isPending}
        >
          {saveMutation.isPending
            ? tr(locale, "جارٍ الحفظ…", "Saving…")
            : tr(locale, "حفظ الرسالة", "Save notice")}
        </Button>
      </div>
    </div>
  );
}
